import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import type { SchedulerJobsResponse, SchedulerJob } from '../lib/api';
import {
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Clock,
  CalendarClock,
  Cpu,
  Zap,
  Circle,
} from 'lucide-react';

const EST_TZ = 'America/New_York';

function fmtEst(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: EST_TZ,
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      timeZoneName: 'short',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function fmtTrigger(raw: string): string {
  // interval[0:20:00] → Every 20 min  |  interval[0:00:30] → Every 30 sec
  const ivMatch = raw.match(/^interval\[(\d+):(\d+):(\d+)\]$/);
  if (ivMatch) {
    const [, hh, mm, ss] = ivMatch;
    const h = parseInt(hh, 10);
    const m = parseInt(mm, 10);
    const s = parseInt(ss, 10);
    if (h > 0) return `Every ${h}h`;
    if (m > 0) return `Every ${m} min`;
    return `Every ${s} sec`;
  }

  // cron[...] → human-readable
  const cronMatch = raw.match(/^cron\[(.+)\]$/);
  if (!cronMatch) return raw;

  const inner = cronMatch[1];
  const parts: Record<string, string> = {};
  for (const kv of inner.split(', ')) {
    const eqIdx = kv.indexOf("='");
    if (eqIdx === -1) continue;
    const key = kv.slice(0, eqIdx).trim();
    const val = kv.slice(eqIdx + 2).replace(/'$/, '');
    parts[key] = val;
  }

  const h   = parts['hour'];
  const m   = parts['minute'];
  const dom = parts['day'];
  const mon = parts['month'];
  const dow = parts['day_of_week'];

  // Yearly: month + day
  if (mon && dom) {
    const t = h && m ? ` at ${h}:${(m).padStart(2, '0')}` : '';
    return `Yearly — ${mon}/${dom}${t}`;
  }

  // Single hour (no commas) → show a nice time
  if (h && !h.includes(',') && m !== undefined) {
    const hour = parseInt(h, 10);
    const min  = parseInt(m, 10);
    const suffix = hour < 12 ? 'AM' : 'PM';
    const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    const time = `${h12}:${min.toString().padStart(2, '0')} ${suffix} EST`;
    const prefix = dow ? 'Weekdays' : 'Daily';
    return `${prefix} at ${time}`;
  }

  // Multiple hours (e.g. market-hours rebalance)
  if (h && h.includes(',') && m !== undefined) {
    const hours = h.split(',').map(Number);
    const min   = parseInt(m, 10);
    const fmt = (hr: number) => {
      const suf = hr < 12 ? 'AM' : 'PM';
      const h12 = hr === 0 ? 12 : hr > 12 ? hr - 12 : hr;
      return `${h12}:${min.toString().padStart(2, '0')} ${suf}`;
    };
    const first = fmt(hours[0]);
    const last  = fmt(hours[hours.length - 1]);
    const freq  = hours.length > 1 ? `${hours.length}×` : '1×';
    const prefix = dow ? 'Weekdays' : 'Daily';
    return `${prefix} ${freq} (${first}–${last} EST)`;
  }

  // Fallback: show the raw inner expression
  return inner;
}

function JobResultBadge({ success }: { success: boolean | null }) {
  if (success === null) return <span className="text-gray-400 text-xs">—</span>;
  return success ? (
    <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
      <CheckCircle className="w-3 h-3" /> OK
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-700 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full">
      <AlertCircle className="w-3 h-3" /> Failed
    </span>
  );
}

function JobRow({ job }: { job: SchedulerJob }) {
  const hasNextRun = !!job.next_run_time;
  return (
    <tr className="hover:bg-gray-50 transition-colors">
      <td className="py-3.5 pr-4 pl-1">
        <p className="font-medium text-gray-900 text-sm">{job.name}</p>
        <p className="text-xs text-gray-400 font-mono mt-0.5">{job.id}</p>
      </td>
      <td className="py-3.5 pr-4">
        <span className="text-xs font-mono text-indigo-700 bg-indigo-50 border border-indigo-100 px-2 py-1 rounded-md whitespace-nowrap">
          {fmtTrigger(job.trigger)}
        </span>
      </td>
      <td className="py-3.5 pr-4">
        {hasNextRun ? (
          <span className="text-sm text-gray-700 whitespace-nowrap">{fmtEst(job.next_run_time)}</span>
        ) : (
          <span className="text-xs text-gray-400 italic">Not scheduled</span>
        )}
      </td>
      <td className="py-3.5 pr-4">
        {job.last_run_at ? (
          <span className="text-sm text-gray-700 whitespace-nowrap">{fmtEst(job.last_run_at)}</span>
        ) : (
          <span className="text-xs text-gray-400 italic">Never</span>
        )}
      </td>
      <td className="py-3.5 pr-4">
        <JobResultBadge success={job.last_run_success} />
      </td>
      <td className="py-3.5 text-xs text-gray-500 max-w-[200px]">
        {job.last_run_detail ?? '—'}
      </td>
    </tr>
  );
}

export function SchedulerAdmin() {
  const [data, setData] = useState<SchedulerJobsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.getSchedulerJobs();
      setData(res);
      setLastRefreshed(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load scheduler jobs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchJobs();
    const interval = setInterval(fetchJobs, 30_000);
    return () => clearInterval(interval);
  }, [fetchJobs]);

  const isRunning = data?.scheduler_running ?? false;
  const isLeader = data?.is_leader ?? false;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Scheduler</h1>
          <p className="text-gray-600 mt-1 text-base">
            All configured cron jobs — last run and next scheduled time in EST
          </p>
        </div>
        <button
          onClick={fetchJobs}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Status bar */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Scheduler status */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <div className="flex items-center gap-2 mb-1">
            <Cpu className="w-4 h-4 text-gray-500" />
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Scheduler</p>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <Circle
              className={`w-3 h-3 fill-current ${isRunning ? 'text-emerald-500' : 'text-gray-300'}`}
            />
            <p className={`text-lg font-bold ${isRunning ? 'text-emerald-700' : 'text-gray-400'}`}>
              {isRunning ? 'Running' : 'Stopped'}
            </p>
          </div>
        </div>

        {/* Leader status */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <div className="flex items-center gap-2 mb-1">
            <Zap className="w-4 h-4 text-gray-500" />
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Instance Role</p>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <Circle
              className={`w-3 h-3 fill-current ${isLeader ? 'text-amber-400' : 'text-gray-300'}`}
            />
            <p className={`text-lg font-bold ${isLeader ? 'text-amber-700' : 'text-gray-400'}`}>
              {isLeader ? 'Leader' : 'Follower'}
            </p>
          </div>
        </div>

        {/* Job count */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <div className="flex items-center gap-2 mb-1">
            <CalendarClock className="w-4 h-4 text-gray-500" />
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Jobs Registered</p>
          </div>
          <p className="text-2xl font-bold text-gray-900 mt-2">{data?.jobs.length ?? '—'}</p>
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-800">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-red-600" />
          {error}
        </div>
      )}

      {/* Jobs table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <CalendarClock className="w-5 h-5 text-indigo-600" />
            Cron Jobs
          </h2>
          {lastRefreshed && (
            <p className="text-xs text-gray-400 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Updated {fmtEst(lastRefreshed.toISOString())}
            </p>
          )}
        </div>

        {loading && !data && (
          <div className="flex items-center justify-center h-40 text-gray-400 text-sm gap-2">
            <RefreshCw className="w-4 h-4 animate-spin" />
            Loading jobs…
          </div>
        )}

        {data && data.jobs.length === 0 && (
          <div className="flex items-center justify-center h-40 text-gray-400 text-sm">
            No jobs registered. Scheduler may not be running on this instance.
          </div>
        )}

        {data && data.jobs.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="text-left py-3 pr-4 pl-6 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Job
                  </th>
                  <th className="text-left py-3 pr-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Schedule
                  </th>
                  <th className="text-left py-3 pr-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Next Run (EST)
                  </th>
                  <th className="text-left py-3 pr-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Last Run (EST)
                  </th>
                  <th className="text-left py-3 pr-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Result
                  </th>
                  <th className="text-left py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Detail
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.jobs.map((job) => (
                  <JobRow key={job.id} job={job} />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Footer note */}
        <div className="px-6 py-3 border-t border-gray-100 text-xs text-gray-400">
          All times shown in Eastern Time (EST/EDT). Page auto-refreshes every 30 seconds.
          Last-run data for heartbeat and token-refresh jobs is not persisted.
        </div>
      </div>
    </div>
  );
}
